import { gql } from 'graphql-request';
import { graphqlClient } from '../client';

// Define collection queries
// Note: This GraphQL query is no longer used - getCollectionsQuery now reads directly from DB
export const GET_COLLECTIONS_QUERY = gql`
  query GetCollections {
    collections {
      id
      name
      description
      owner
      created_at
      updated_at
      canva_folder_name
      canva_folder_url
      canva_folder_id
      canva_design_thumbnail
      assets {
        id
      }
    }
  }
`;

export const GET_COLLECTION_QUERY = gql`
  query GetCollection($id: ID!) {
    collection(id: $id) {
      id
      name
      description
      owner
      created_at
      updated_at
      canva_folder_name
      canva_folder_url
      canva_folder_id
      assets {
        id
        collection_id
        type
        page_id
        slug
        version
        title
        description
        primary_image
        secondary_image
        primary_original_url
        primary_cropped_url
        primary_markup_url
        secondary_original_url
        secondary_cropped_url
        secondary_markup_url
        crop_aspect_ratio
        crop_coordinates
        secondary_version
        secondary_page_id
        primary_annotations
        secondary_annotations
        created_at
        updated_at
        page {
          id
          subslug
          pricing_page_url
        }
      }
    }
  }
`;

export const GET_ASSETS_QUERY = gql`
  query GetAssets($collection_id: ID) {
    assets(collection_id: $collection_id) {
      id
      collection_id
      type
      page_id
      slug
      version
      title
      description
      primary_image
      secondary_image
      primary_original_url
      primary_cropped_url
      primary_markup_url
      secondary_original_url
      secondary_cropped_url
      secondary_markup_url
      crop_aspect_ratio
      crop_coordinates
      secondary_version
      secondary_page_id
      primary_annotations
      secondary_annotations
      created_at
      updated_at
      collection {
        id
        name
      }
      page {
        id
        subslug
        pricing_page_url
      }
    }
  }
`;

// Define response types
export interface Asset {
  id: string;
  collection_id: string;
  type: 'simple' | 'diff';
  page_id: string;
  slug: string;
  version: string;
  title: string;
  description?: string;
  primary_image?: string;
  secondary_image?: string;
  primary_original_url?: string;
  primary_cropped_url?: string;
  primary_markup_url?: string;
  secondary_original_url?: string;
  secondary_cropped_url?: string;
  secondary_markup_url?: string;
  crop_aspect_ratio?: string;
  crop_coordinates?: any;
  // Dual image comparison fields (not fetched via GraphQL due to backend resolver issues)
  comparison_mode?: 'single' | 'side-by-side' | 'stacked';
  secondary_version?: string;
  secondary_page_id?: string;
  primary_annotations?: any;
  secondary_annotations?: any;
  created_at: string;
  updated_at: string;
  collection?: {
    id: string;
    name: string;
  };
  page?: {
    id: string;
    subslug: string;
    pricing_page_url?: string;
  };
}

export interface Collection {
  id: string;
  name: string;
  description?: string;
  owner: string;
  created_at: string;
  updated_at: string;
  published_at?: string;
  canva_folder_name?: string;
  canva_folder_url?: string;
  canva_folder_id?: string;
  canva_design_thumbnail?: string;
  assets?: Asset[];
}

export interface GetCollectionsResponse {
  collections: Collection[];
}

export interface GetCollectionResponse {
  collection: Collection;
}

export interface GetAssetsResponse {
  assets: Asset[];
}

/**
 * Fetches all collections for the current user (direct database access)
 * @returns Promise with collections data
 */
export async function getCollectionsQuery(): Promise<GetCollectionsResponse> {
  try {
    console.log('🔍 [DB] Fetching collections...');

    const { supabase } = await import('@/integrations/supabase/client');

    // Fetch collections with asset count
    const { data: collections, error } = await supabase
      .from('app_collections')
      .select(`
        id,
        name,
        description,
        owner,
        created_at,
        updated_at,
        published_at,
        canva_folder_name,
        canva_folder_url,
        canva_folder_id,
        canva_design_thumbnail,
        app_assets (id)
      `)
      .order('published_at', { ascending: false, nullsFirst: false })
      .order('updated_at', { ascending: false });

    if (error) {
      console.error('❌ [DB] Error fetching collections:', error);
      throw error;
    }

    // Transform the response to match the expected format
    const transformedCollections: Collection[] = (collections || []).map(c => ({
      id: c.id,
      name: c.name,
      description: c.description,
      owner: c.owner,
      created_at: c.created_at,
      updated_at: c.updated_at,
      published_at: c.published_at,
      canva_folder_name: c.canva_folder_name,
      canva_folder_url: c.canva_folder_url,
      canva_folder_id: c.canva_folder_id,
      canva_design_thumbnail: c.canva_design_thumbnail,
      assets: c.app_assets?.map((a: { id: string }) => ({ id: a.id })) || [],
    }));

    console.log(`✅ [DB] Successfully fetched ${transformedCollections.length} collections`);
    return { collections: transformedCollections };
  } catch (error: any) {
    console.error('❌ [DB] Error fetching collections:', error);
    throw error;
  }
}

/**
 * Fetches a single collection with its assets
 * @param id The collection ID
 * @returns Promise with collection data
 */
export async function getCollectionQuery(id: string): Promise<GetCollectionResponse> {
  try {
    console.log(`🔍 [GraphQL] Fetching collection ${id}...`);

    const response = await graphqlClient.request<GetCollectionResponse>(GET_COLLECTION_QUERY, { id });

    console.log('✅ [GraphQL] Successfully fetched collection:', response.collection?.name);
    return response;
  } catch (error: any) {
    console.error('❌ [GraphQL] Error fetching collection:', error);
    
    if (error.message?.includes('Collection not found')) {
      throw new Error('Collection not found. It may have been deleted.');
    } else if (error.message?.includes('network')) {
      throw new Error('Network error: Could not reach the GraphQL server');
    } else {
      throw error;
    }
  }
}

/**
 * Fetches assets, optionally filtered by collection
 * @param collection_id Optional collection ID to filter by
 * @returns Promise with assets data
 */
export async function getAssetsQuery(collection_id?: string): Promise<GetAssetsResponse> {
  try {
    console.log('🔍 [GraphQL] Fetching assets...', collection_id ? `for collection ${collection_id}` : 'all');

    const response = await graphqlClient.request<GetAssetsResponse>(
      GET_ASSETS_QUERY, 
      { collection_id: collection_id || undefined }
    );

    console.log(`✅ [GraphQL] Successfully fetched ${response.assets?.length || 0} assets`);
    return response;
  } catch (error: any) {
    console.error('❌ [GraphQL] Error fetching assets:', error);
    
    if (error.message?.includes('network')) {
      throw new Error('Network error: Could not reach the GraphQL server');
    } else {
      throw error;
    }
  }
}